import { Controller } from "@hotwired/stimulus";

type Month = {
  year: number;
  month: number;
};

const MONTH_PLACEHOLDER = "__MONTH__";
const DATE_FROM_PLACEHOLDER = "__DATE_FROM__";
const DATE_TO_PLACEHOLDER = "__DATE_TO__";
const EDGE_THRESHOLD_PX = 16;
const SCROLL_STOP_DEBOUNCE_MS = 200;
const TRAILING_SPACER_WIDTH = "50vw";

export default class extends Controller<HTMLElement> {
  static targets = ["list", "template"];
  static values = {
    minMonth: String,
    maxMonth: String,
    hrefTemplate: String,
    focusMonth: String,
    activeMonth: String,
    batchSize: { type: Number, default: 12 },
    locale: String,
    monthLabels: Array,
  };

  declare readonly listTarget: HTMLElement;
  declare readonly templateTarget: HTMLTemplateElement;
  declare readonly minMonthValue: string;
  declare readonly maxMonthValue: string;
  declare readonly hrefTemplateValue: string;
  declare readonly focusMonthValue: string;
  declare readonly activeMonthValue: string;
  declare readonly batchSizeValue: number;
  declare readonly localeValue: string;
  declare readonly monthLabelsValue: string[];

  private readonly onScrollBound = this.onScroll.bind(this);
  private readonly onScrollEndBound = this.onScrollEnd.bind(this);
  private readonly onResizeBound = this.onResize.bind(this);
  private scrollStopTimeout: number | undefined;
  private resizeAnimationFrame: number | undefined;

  connect(): void {
    this.element.addEventListener("scroll", this.onScrollBound, {
      passive: true,
    });
    this.element.addEventListener("scrollend", this.onScrollEndBound);
    window.addEventListener("resize", this.onResizeBound);

    requestAnimationFrame(() => this.initializeScrollPosition());
  }

  disconnect(): void {
    this.element.removeEventListener("scroll", this.onScrollBound);
    this.element.removeEventListener("scrollend", this.onScrollEndBound);
    window.removeEventListener("resize", this.onResizeBound);

    if (this.scrollStopTimeout !== undefined) {
      window.clearTimeout(this.scrollStopTimeout);
    }
    if (this.resizeAnimationFrame !== undefined) {
      window.cancelAnimationFrame(this.resizeAnimationFrame);
    }
  }

  private onScroll(): void {
    if (this.scrollStopTimeout !== undefined) {
      window.clearTimeout(this.scrollStopTimeout);
    }

    this.scrollStopTimeout = window.setTimeout(() => {
      this.scrollStopTimeout = undefined;
      this.loadVisibleEdgeMonths();
    }, SCROLL_STOP_DEBOUNCE_MS);
  }

  private onScrollEnd(): void {
    if (this.scrollStopTimeout !== undefined) {
      window.clearTimeout(this.scrollStopTimeout);
      this.scrollStopTimeout = undefined;
    }

    this.loadVisibleEdgeMonths();
  }

  private onResize(): void {
    if (this.resizeAnimationFrame !== undefined) {
      window.cancelAnimationFrame(this.resizeAnimationFrame);
    }

    this.resizeAnimationFrame = window.requestAnimationFrame(() => {
      this.resizeAnimationFrame = undefined;
      this.refreshTrailingSpacer();
      this.centerFocusMonth();
    });
  }

  private initializeScrollPosition(): void {
    this.removeTrailingSpacer();

    while (true) {
      this.centerFocusMonth();

      if (this.loadPastMonthsIfNeeded()) {
        continue;
      }
      if (this.loadFutureMonthsIfNeeded()) {
        continue;
      }

      break;
    }

    this.refreshTrailingSpacer();
    this.centerFocusMonth();
  }

  private loadVisibleEdgeMonths(): void {
    if (this.hasMoreFutureMonths()) {
      this.removeTrailingSpacer();
    }

    while (this.loadEdgeMonthsIfNeeded()) {
      // Keep loading until the visible edge is backed by either real months or
      // the configured min/max month boundary.
    }

    this.refreshTrailingSpacer();
  }

  private loadEdgeMonthsIfNeeded(): boolean {
    const loadedPastMonths = this.loadPastMonthsIfNeeded();
    const loadedFutureMonths = this.loadFutureMonthsIfNeeded();

    return loadedPastMonths || loadedFutureMonths;
  }

  private loadPastMonthsIfNeeded(): boolean {
    if (!this.isAtLeftEdge()) {
      return false;
    }

    const firstRenderedMonth = this.firstRenderedMonth();
    if (firstRenderedMonth === null) {
      return false;
    }

    const minMonth = parseMonth(this.minMonthValue);
    if (compareMonths(firstRenderedMonth, minMonth) <= 0) {
      return false;
    }

    const monthsToPrepend: Month[] = [];
    for (let i = 1; i <= this.batchSizeValue; i += 1) {
      const month = addMonths(firstRenderedMonth, -i);
      if (compareMonths(month, minMonth) < 0) {
        break;
      }
      monthsToPrepend.push(month);
    }

    if (monthsToPrepend.length === 0) {
      return false;
    }

    const previousScrollWidth = this.element.scrollWidth;
    const fragment = document.createDocumentFragment();

    monthsToPrepend.reverse().forEach((month) => {
      fragment.append(this.createMonthLink(month));
    });

    this.listTarget.prepend(fragment);

    const addedWidth = this.element.scrollWidth - previousScrollWidth;
    this.element.scrollLeft += addedWidth;

    return true;
  }

  private loadFutureMonthsIfNeeded(): boolean {
    if (!this.isAtRightEdge()) {
      return false;
    }

    const lastRenderedMonth = this.lastRenderedMonth();
    if (lastRenderedMonth === null) {
      return false;
    }

    const maxMonth = parseMonth(this.maxMonthValue);
    if (compareMonths(lastRenderedMonth, maxMonth) >= 0) {
      return false;
    }

    const monthsToAppend: Month[] = [];
    for (let i = 1; i <= this.batchSizeValue; i += 1) {
      const month = addMonths(lastRenderedMonth, i);
      if (compareMonths(month, maxMonth) > 0) {
        break;
      }
      monthsToAppend.push(month);
    }

    if (monthsToAppend.length === 0) {
      return false;
    }

    const fragment = document.createDocumentFragment();
    monthsToAppend.forEach((month) => {
      fragment.append(this.createMonthLink(month));
    });

    const spacer = this.trailingSpacerElement();
    if (spacer === null) {
      this.listTarget.append(fragment);
    } else {
      this.listTarget.insertBefore(fragment, spacer);
    }

    return true;
  }

  private refreshTrailingSpacer(): void {
    if (!this.hasReachedFutureLimit()) {
      this.removeTrailingSpacer();
      return;
    }

    this.ensureTrailingSpacer();
  }

  private ensureTrailingSpacer(): void {
    const spacer =
      this.trailingSpacerElement() ?? this.createTrailingSpacerElement();
    spacer.style.flex = `0 0 ${TRAILING_SPACER_WIDTH}`;
    spacer.style.width = TRAILING_SPACER_WIDTH;
  }

  private removeTrailingSpacer(): void {
    this.trailingSpacerElement()?.remove();
  }

  private trailingSpacerElement(): HTMLElement | null {
    return this.listTarget.querySelector<HTMLElement>(
      "[data-month-strip-trailing-spacer]",
    );
  }

  private createTrailingSpacerElement(): HTMLElement {
    const spacer = document.createElement("div");
    spacer.dataset.monthStripTrailingSpacer = "true";
    spacer.setAttribute("aria-hidden", "true");
    spacer.setAttribute("role", "presentation");
    spacer.style.pointerEvents = "none";

    this.listTarget.append(spacer);

    return spacer;
  }

  private centerFocusMonth(): void {
    const focusedMonth = this.focusedMonthElement();
    if (focusedMonth === null) {
      return;
    }

    const desiredLeft =
      focusedMonth.offsetLeft -
      (this.element.clientWidth - focusedMonth.offsetWidth) / 2;
    const maxLeft = this.element.scrollWidth - this.element.clientWidth;

    this.element.scrollLeft = Math.max(0, Math.min(desiredLeft, maxLeft));
  }

  private isAtLeftEdge(): boolean {
    return this.element.scrollLeft <= EDGE_THRESHOLD_PX;
  }

  private isAtRightEdge(): boolean {
    const maxLeft = this.element.scrollWidth - this.element.clientWidth;
    return maxLeft - this.element.scrollLeft <= EDGE_THRESHOLD_PX;
  }

  private hasMoreFutureMonths(): boolean {
    const lastRenderedMonth = this.lastRenderedMonth();
    if (lastRenderedMonth === null) {
      return false;
    }

    return compareMonths(lastRenderedMonth, parseMonth(this.maxMonthValue)) < 0;
  }

  private hasReachedFutureLimit(): boolean {
    const lastRenderedMonth = this.lastRenderedMonth();
    if (lastRenderedMonth === null) {
      return false;
    }

    return compareMonths(lastRenderedMonth, parseMonth(this.maxMonthValue)) >= 0;
  }

  private focusedMonthElement(): HTMLElement | null {
    return (
      Array.from(
        this.listTarget.querySelectorAll<HTMLElement>("[data-month-strip-month]"),
      ).find(
        (monthElement) =>
          monthElement.dataset.monthStripMonth === this.focusMonthValue,
      ) ?? null
    );
  }

  private firstRenderedMonth(): Month | null {
    const firstMonthLink = this.listTarget.querySelector<HTMLElement>(
      "[data-month-strip-month]",
    );
    if (firstMonthLink === null) {
      return null;
    }

    return parseMonth(firstMonthLink.dataset.monthStripMonth ?? "");
  }

  private lastRenderedMonth(): Month | null {
    const monthLinks = this.listTarget.querySelectorAll<HTMLElement>(
      "[data-month-strip-month]",
    );
    if (monthLinks.length === 0) {
      return null;
    }

    const lastMonthLink = monthLinks.item(monthLinks.length - 1);
    return parseMonth(lastMonthLink.dataset.monthStripMonth ?? "");
  }

  private createMonthLink(month: Month): HTMLAnchorElement {
    const clone = this.templateTarget.content.firstElementChild?.cloneNode(
      true,
    );
    if (!(clone instanceof HTMLAnchorElement)) {
      throw new Error("month-strip template must contain an anchor element");
    }

    const monthString = formatMonthStart(month);
    clone.setAttribute("href", this.hrefFor(month));
    clone.dataset.monthStripMonth = monthString;

    const label = clone.querySelector<HTMLElement>("[data-month-strip-label]");
    if (label !== null) {
      label.textContent = this.labelFor(month);
    } else {
      clone.textContent = this.labelFor(month);
    }

    if (this.activeMonthValue === monthString) {
      clone.setAttribute("aria-current", "date");
    } else if (this.focusMonthValue === monthString) {
      clone.dataset.budgeterButtonAppearance = "soft";
      clone.dataset.monthStripFocus = "true";
    }

    return clone;
  }

  private hrefFor(month: Month): string {
    return this.hrefTemplateValue
      .replaceAll(MONTH_PLACEHOLDER, formatMonthStart(month))
      .replaceAll(DATE_FROM_PLACEHOLDER, formatMonthStart(month))
      .replaceAll(DATE_TO_PLACEHOLDER, formatMonthEnd(month));
  }

  private labelFor(month: Month): string {
    const monthLabel = this.monthLabelsValue[month.month - 1];
    if (monthLabel !== undefined) {
      return `${monthLabel} ${month.year}`;
    }

    return new Intl.DateTimeFormat(this.localeValue || undefined, {
      month: "short",
      year: "numeric",
      timeZone: "UTC",
    }).format(new Date(Date.UTC(month.year, month.month - 1, 1)));
  }
}

function parseMonth(value: string): Month {
  const match = /^(\d{4})-(\d{2})-\d{2}$/.exec(value);
  if (match === null) {
    return { year: 1970, month: 1 };
  }

  return {
    year: Number.parseInt(match[1], 10),
    month: Number.parseInt(match[2], 10),
  };
}

function compareMonths(a: Month, b: Month): number {
  return monthIndex(a) - monthIndex(b);
}

function addMonths(month: Month, offset: number): Month {
  const index = monthIndex(month) + offset;
  return {
    year: Math.floor(index / 12),
    month: (index % 12) + 1,
  };
}

function monthIndex(month: Month): number {
  return month.year * 12 + month.month - 1;
}

function formatMonthStart(month: Month): string {
  return `${month.year.toString().padStart(4, "0")}-${month.month
    .toString()
    .padStart(2, "0")}-01`;
}

function formatMonthEnd(month: Month): string {
  const lastDay = new Date(Date.UTC(month.year, month.month, 0)).getUTCDate();
  return `${month.year.toString().padStart(4, "0")}-${month.month
    .toString()
    .padStart(2, "0")}-${lastDay.toString().padStart(2, "0")}`;
}
